<template>
    <div class="sc-adam-image-picker">

        <div class="sc-adam-image-picker__input-box">
            <el-input
                    v-model="input"
                    class="sc-adam-image-picker__input"
                    :placeholder="labels('inputPlaceholder')"
            >
                <template slot="prepend">{{ labels('input') }}</template>
            </el-input>
        </div>

        <div
                v-loading="loading"
                class="sc-adam-image-picker__image-box">

            <div class="sc-adam-image-picker__placeholder">
                <img
                        v-if="showImage"
                        :src="image"
                        alt="image"
                />

                <div
                        v-if="!initialized"
                        class="sc-adam-image-picker__info"
                >
                    <span v-html="labels('info')"></span>
                    <span class="aprimo-link">
                        <a
                                :href="aprimoLink"
                                target="_blank"
                        >
                            {{ labels('infoLink') }}
                        </a>
                    </span>
                </div>

                <div
                        v-if="error"
                        class="sc-adam-image-picker__error"
                >
                    {{ labels('error') }}
                </div>
            </div>
        </div>

        <div class="sc-adam-image-picker__button-box">
            <span class="version">{{ version }}</span>
            <el-button @click="onCancelClicked">{{ labels('buttonCancel') }}</el-button>
            <el-button
                    @click="useImage"
                    :disabled="savingDisabled"
                    type="primary">{{ labels('buttonConfirm') }}
            </el-button>
        </div>

    </div>
</template>

<style lang="stylus" scoped>
    .sc-adam-image-picker {
        height 100vh
        width 100vw
        display flex
        flex-direction column

        --max-ui-width 600px

        span.aprimo-link {
            display block
            text-align center
            margin-top 20px
        }

        a, a:visited, a:active, a:focus {
            color #3F51B5
        }

        &__input-box {
            padding 32px
            display flex
            align-items center
            justify-content center
        }

        &__image-box {
            position relative
            flex-grow 1
            background #F5F7FA
            overflow hidden
        }

        &__placeholder {
            --line-color-vis rgba(220, 223, 230, 1)
            --line-color rgba(220, 223, 230, 0)
            --line-width 2px

            position relative
            width 100%
            height 100%

            display flex
            align-items center
            justify-content center

            img {
                max-width 100%
            }

            background linear-gradient(to top left,
            var(--line-color) 0%,
            var(--line-color) calc(50% - var(--line-width)),
            var(--line-color-vis) 50%,
            var(--line-color) calc(50% + var(--line-width)),
            var(--line-color) 100%),
            linear-gradient(to top right,
            var(--line-color) 0%,
            var(--line-color) calc(50% - var(--line-width)),
            var(--line-color-vis) 50%,
            var(--line-color) calc(50% + var(--line-width)),
            var(--line-color) 100%)
        }

        &__button-box {
            display flex
            align-items center
            justify-content flex-end
            padding 16px 32px

            > span.version {
                margin-right auto
                font-size 12px
                opacity .6
            }
        }

        &__error {
            padding 16px
            background #fe6c6f
            border-radius 12px
            color #fff
        }

        &__info {
            text-align center
            padding 16px
            background #fff
            border-radius 12px
        }
    }
</style>

<script>
  import { route } from '../constants'

  const REGEX_APRIMO_ASSET_URL = /.*\/~?(.+?)$/i
  let _debounce = null

  export default {
    name: 'ImagePicker',
    data () {
      return {
        input: '',
        loading: false,
        error: false,
        image: null,
        initialized: false
      }
    },

    computed: {
      version () {
        return process.env.VUE_APP_VERSION
          ? 'v' + process.env.VUE_APP_VERSION
          : ''
      },

      savingDisabled () {
        return this.loading || this.error || !this.image
      },

      showImage () {
        return !this.error && this.image
      },

      aprimoLink () {
        // todo we need a generic link
        return this.$root.env.aprimoLink || '#'
      }
    },

    watch: {
      input () {
        if (_debounce) {
          window.clearTimeout(_debounce)
          _debounce = null
        }

        _debounce = window.setTimeout(this.searchImage, 500)
      }
    },

    methods: {
      useImage () {
        window.parent.postMessage({
          command: 'resolveBriefingServiceResult',
          argument: this.image
        }, '*')
      },

      searchImage () {
        if (this.input.length === 0) {
          // nope
          return
        }

        // for the first time we do initialize it
        this.initialized = true
        this.loading = true
        this.error = false
        this.image = null

        // lets fetch the image
        fetch(route('/api/images/' + this.input), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        })
          .then(r => {
            if (r.status !== 200) {
              this.error = true
              return null
            }

            return r.json()
          })
          .then(r => {
            this.loading = false
            this.image = r.image
          })
      },

      onPaste (e) {
        const paste = (e.clipboardData || window.clipboardData).getData('text')
        const matches = REGEX_APRIMO_ASSET_URL.exec(paste)

        if (matches !== null) {
          e.preventDefault()

          this.input = matches[1]
          this.$message('You\'ve pasted an ' + this.labels('name') + ' Url – we are searching...')
        }
      },

      onCancelClicked () {
        window.parent.postMessage({
          command: 'rejectBriefingServiceResult',
          argument: 'canceled'
        }, '*')
      },

      labels (label) {
        // todo todo combine all labels in labels object
        return this.$root.env[label] || ''
      }
    },

    created () {
      window.addEventListener('paste', this.onPaste)
    },

    beforeDestroy () {
      window.removeEventListener('paste', this.onPaste)
    }
  }
</script>
